import type { PublicationModel } from "../../types/models";
import { COMMITTED_PUBLICATION_STATUSES } from "../../utils/PublicationHelper";

// §12 read face project chip. The island's first slot holds "what" in both
// faces: the shorts picker in the edit face, the project in the read face.
// The queue is always ONE folder's plan (§16.1), so the chip names the
// project that plan belongs to — the one fact the calendar entry never
// states — and lists every other project with committed posts to switch
// to. Pure derivations here; `useScheduledProjects` fetches the
// video → folder and folder rows they read.

export type ScheduledProject = {
  folderId: string;
  name: string;
  thumbnailUrl: string | null;
  /** Committed posts still to go live: scheduled, publishing, verifying. */
  upcomingCount: number;
  /** The earliest upcoming post's date. */
  nextScheduledAt: Date | null;
  /** Posts already live. */
  publishedCount: number;
  /** The latest published post's date. */
  lastPublishedAt: Date | null;
};

export type ScheduledProjectFolder = {
  id: string;
  name: string | null;
  thumbnailUrl: string | null;
};

/**
 * How far back a project's posts still list it in the picker: the
 * calendar's recent past. A project whose last post went live before
 * this is done, and the calendar page is its home. Bounds the video →
 * folder lookup, not the counts: once a folder lists, every committed
 * post of its shorts counts, so its "last" date is exact.
 */
export const SCHEDULED_PROJECTS_HISTORY_DAYS = 90;

export const UNTITLED_PROJECT_NAME = "Untitled project";

const toTime = (value: Date | string | number | null | undefined) => {
  if (value == null) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const isCommitted = (pub: PublicationModel) =>
  COMMITTED_PUBLICATION_STATUSES.has(pub.status);

/** Committed but not live yet. A missed slot still awaiting publish is
 *  upcoming in intent, so this reads the status, not the clock. */
const isUpcoming = (pub: PublicationModel) => pub.status !== "published";

export const normalizeProjectName = (name: string | null | undefined) =>
  (name ?? "").trim() || UNTITLED_PROJECT_NAME;

/** Distinct ids of the shorts holding a committed post inside the history
 *  window, sorted — the hook's video → folder lookup, and its query key. */
export const selectScheduledProjectVideoIds = (
  publications: readonly PublicationModel[],
  now: number,
): string[] => {
  const floor = now - SCHEDULED_PROJECTS_HISTORY_DAYS * 24 * 60 * 60 * 1000;
  const ids = new Set<string>();
  for (const pub of publications) {
    if (!pub.projectId || !isCommitted(pub)) continue;
    const time = toTime(pub.scheduledAt);
    if (time === null || time < floor) continue;
    ids.add(pub.projectId);
  }
  return [...ids].sort();
};

/** Upcoming projects first, next post soonest first; then done projects,
 *  most recently live first; names break ties so the order is stable. */
export const compareScheduledProjects = (
  a: ScheduledProject,
  b: ScheduledProject,
): number => {
  const aUpcoming = a.upcomingCount > 0;
  const bUpcoming = b.upcomingCount > 0;
  if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1;
  if (aUpcoming) {
    const delta =
      (a.nextScheduledAt?.getTime() ?? Infinity) -
      (b.nextScheduledAt?.getTime() ?? Infinity);
    if (delta) return delta;
  } else {
    const delta =
      (b.lastPublishedAt?.getTime() ?? -Infinity) -
      (a.lastPublishedAt?.getTime() ?? -Infinity);
    if (delta) return delta;
  }
  return a.name.localeCompare(b.name);
};

/** Every folder with at least one committed post among the mapped shorts,
 *  in picker order. A folder the index knows without a committed post (a
 *  schedule entry still holding drafts) has no plan to show and is left
 *  out; the read face never renders for it anyway. */
export const buildScheduledProjects = ({
  publications,
  folderIdByVideoId,
  folderById,
}: {
  publications: readonly PublicationModel[];
  folderIdByVideoId: Record<string, string>;
  folderById: Record<string, ScheduledProjectFolder>;
}): ScheduledProject[] => {
  const byFolder = new Map<
    string,
    { upcoming: number; next: number | null; published: number; last: number | null }
  >();
  for (const pub of publications) {
    if (!pub.projectId || !isCommitted(pub)) continue;
    const folderId = folderIdByVideoId[pub.projectId];
    if (!folderId) continue;
    const time = toTime(pub.scheduledAt);
    const acc = byFolder.get(folderId) ?? {
      upcoming: 0,
      next: null,
      published: 0,
      last: null,
    };
    if (isUpcoming(pub)) {
      acc.upcoming += 1;
      if (time !== null && (acc.next === null || time < acc.next)) acc.next = time;
    } else {
      acc.published += 1;
      if (time !== null && (acc.last === null || time > acc.last)) acc.last = time;
    }
    byFolder.set(folderId, acc);
  }
  const projects: ScheduledProject[] = [...byFolder.entries()].map(
    ([folderId, acc]) => ({
      folderId,
      name: normalizeProjectName(folderById[folderId]?.name),
      thumbnailUrl: folderById[folderId]?.thumbnailUrl ?? null,
      upcomingCount: acc.upcoming,
      nextScheduledAt: acc.next === null ? null : new Date(acc.next),
      publishedCount: acc.published,
      lastPublishedAt: acc.last === null ? null : new Date(acc.last),
    }),
  );
  projects.sort(compareScheduledProjects);
  return projects;
};

/**
 * The short a switched-to project opens on: the one holding its next
 * upcoming post, else its most recently live one. The rest of the folder's
 * committed shorts ride along as context rows (§16.1), so this only picks
 * where the current-video index lands. Null when nothing is committed.
 */
export const pickScheduleEntryVideoId = ({
  publications,
  videoIds,
}: {
  publications: readonly PublicationModel[];
  videoIds: readonly string[];
}): string | null => {
  const folderVideoIds = new Set(videoIds);
  let upcoming: { videoId: string; time: number } | null = null;
  let published: { videoId: string; time: number } | null = null;
  for (const pub of publications) {
    if (!pub.projectId || !folderVideoIds.has(pub.projectId)) continue;
    if (!isCommitted(pub)) continue;
    const time = toTime(pub.scheduledAt);
    if (time === null) continue;
    if (isUpcoming(pub)) {
      if (!upcoming || time < upcoming.time)
        upcoming = { videoId: pub.projectId, time };
    } else if (!published || time > published.time) {
      published = { videoId: pub.projectId, time };
    }
  }
  return upcoming?.videoId ?? published?.videoId ?? null;
};

/** A picker row's trailing caption: the count behind the project, in the
 *  dialog title's own word ("Scheduled posts" / "Posted posts"), so the
 *  row and the title it opens rhyme. Count only — the caption sits beside
 *  the name on one line, like the editor picker's "#3", and a date there
 *  crowded the name out; "scheduled" already says upcoming and
 *  "posted" already says past. A live project counts only what is
 *  still to come — that is what matters when choosing. */
export const describeScheduledProject = (
  project: Pick<ScheduledProject, "upcomingCount" | "publishedCount">,
): string => {
  if (project.upcomingCount > 0) return `${project.upcomingCount} scheduled`;
  if (project.publishedCount > 0) return `${project.publishedCount} posted`;
  return "No posts";
};
